// miniapp/src/client/App.tsx  (обновлённый — добавлен импорт и роут Reminders)
import { useEffect } from "react";
import type { JSX } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";

import AppShell from "./AppShell";
import FaceControl from "./door/FaceControl";
import { api } from "./lib/api";
import { ROUTE } from "./lib/paths";
import { BASE } from "./lib/config";
import RoleWatcher from "./components/RoleWatcher";
import SuperAdminHome from "./super/SuperAdminHome";
import { normalizeRole, homeRouteFor } from "./lib/rbac";
import RequireAccess from "./components/RequireAccess";
import StaffList from "./super/StaffList"
import StaffRoles from "./super/StaffRoles"

import EventsList from "./super/EventsList";
import EventCreate from "./super/EventCreate";
import EventEdit from "./super/EventEdit";
import Messages from "./super/Messages";
import PromoCodes from "./super/PromoCodes";
import Settings from "./super/Settings";

import GuestFlow from "./guest/GuestFlow";
import PaySuccess from "./guest/PaySuccess";

function PayResult({ ok }: { ok: boolean }) {
  const title = ok ? "Оплата прошла успешно" : "Оплата не выполнена";
  const note  = ok
    ? "Спасибо! Платёж зафиксирован. Вернитесь в бот и дождитесь обновления статуса пропуска."
    : "Похоже, платёж не завершился. Вернитесь и попробуйте снова.";
  return (
    <div style={{minHeight:"100vh",display:"grid",placeItems:"center",background:"linear-gradient(180deg,#0b1020 0%,#141a2b 100%)",color:"#eaf2ff"}}>
      <div style={{maxWidth:720,margin:"0 auto",padding:24,background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.1)",borderRadius:20}}>
        <h1 style={{margin:"8px 0 16px"}}>{title}</h1>
        <p style={{opacity:.85}}>{note}</p>
      </div>
    </div>
  );
}
function PayStub() {
  return (
    <div style={{minHeight:"100vh",display:"grid",placeItems:"center",background:"linear-gradient(180deg,#0b1020 0%,#141a2b 100%)",color:"#eaf2ff"}}>
      <div style={{maxWidth:720,margin:"0 auto",padding:24,background:"rgba(255,255,255,.06)",border:"1px solid rgba(255,255,255,.1)",borderRadius:20}}>
        <h1 style={{margin:"8px 0 16px"}}>Страница оплаты</h1>
        <p style={{opacity:.85}}>
          Эта сервисная страница работает независимо от Telegram. Если вы попали сюда напрямую —
          вернитесь в приложение и запустите оплату из него.
        </p>
      </div>
    </div>
  );
}

function RootRedirect() {
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const me = await api.me();
        if (!alive || loc.pathname !== "/") return;

        const role = normalizeRole(me.role);
        if (role === "owner" || role === "superadmin" || role === "admin" || role === "door") {
          nav(ROUTE.toPath(homeRouteFor(role)), { replace: true });
          return;
        }
        nav(ROUTE.toPath(ROUTE.guest()), { replace: true });
      } catch {
        if (loc.pathname === "/") nav(ROUTE.toPath(ROUTE.guest()), { replace: true });
      }
    })();

    return () => { alive = false; };
  }, [loc.pathname, nav]);

  return null;
}

function Shell({
  children,
  disableTopInset = false,
}: {
  children: JSX.Element;
  disableTopInset?: boolean;
}) {
  return (
    <AppShell disableTopInset={disableTopInset}>
      {children}
    </AppShell>
  );
}

export default function App() {
  return (
    <BrowserRouter basename={BASE}>
      <RoleWatcher />
      <Routes>
        <Route path="/" element={<RootRedirect />} />

        {/* Суперадмин/Админ */}
        <Route path="/super" element={<Shell><SuperAdminHome /></Shell>} />
        <Route path="/super/events" element={<Shell><EventsList /></Shell>} />
        <Route path="/super/events/new" element={<Shell><EventCreate /></Shell>} />
        <Route path="/super/events/:id/edit" element={<Shell><EventEdit /></Shell>} />
        <Route path="/super/staff" element={<Shell><StaffList /></Shell>} />
        <Route path="/super/staff/roles" element={<Shell><StaffRoles /></Shell>} />
        <Route path="/super/messages" element={<Shell><Messages /></Shell>} />
        <Route path="/super/promo" element={<Shell><PromoCodes /></Shell>} />
        <Route path="/super/settings" element={<Shell><Settings /></Shell>} />

        {/* Door */}
        <Route path="/door" element={<RequireAccess><Shell><FaceControl /></Shell></RequireAccess>} />
        <Route path="/door/scan" element={<RequireAccess><Shell><FaceControl /></Shell></RequireAccess>} />
        <Route path="/door/t/:token" element={<RequireAccess><Shell><FaceControl /></Shell></RequireAccess>} />

        {/* Гости */}
        <Route path="/guest" element={<Shell disableTopInset><GuestFlow /></Shell>} />
        <Route path="/guest/pay/success" element={<PaySuccess />} />

        {/* Алиасы */}
        <Route path="/eula" element={<Shell disableTopInset><GuestFlow /></Shell>} />

        {/* Страницы /pay* */}
        <Route path="/pay" element={<PayStub />} />
        <Route path="/pay/success" element={<PayResult ok />} />
        <Route path="/pay/fail" element={<PayResult ok={false} />} />

        <Route path="*" element={<Navigate to={ROUTE.toPath(ROUTE.guest())} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
